// Quality Signals — reports feed (primary surface).
// A reverse-chronological list of agent-delivered reports; each entry links to
// a standalone report page that opens in a new tab.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { SignalsTabs } from '../components/signals/SignalsTabs';
import { PortalSignalsTabs } from '../components/signals/PortalSignalsTabs';
import { cadenceLabel } from './signalsReportsUtils';
import {
  signalsReportsApi,
  type SignalReport,
  type SignalReportListParams,
} from '../services/signalsReports';
import styles from './SignalsReportsPage.module.css';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function formatDay(iso: string): { day: string; mon: string } {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return { day: '–', mon: '' };
  return { day: String(d.getUTCDate()), mon: MONTHS[d.getUTCMonth()] ?? '' };
}

export function SignalsReportsPage({
  variant = 'admin',
}: { variant?: 'admin' | 'portal' } = {}) {
  // Portal (reader) variant: portal sub-nav, no admin "Run report" button, and
  // the feed hides "quiet day" reports so an executive sees only substance.
  const isPortal = variant === 'portal';
  const [items, setItems] = useState<SignalReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [customer, setCustomer] = useState('');
  const [theme, setTheme] = useState('');
  const [runNote, setRunNote] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const runNow = useCallback(async () => {
    setRunning(true);
    setRunNote(null);
    try {
      const res = await signalsReportsApi.run();
      setRunNote(res.detail);
    } catch {
      setRunNote('Could not queue the report run — are you an admin, and is the worker up?');
    } finally {
      setRunning(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Roadmap reports share the store but have their own feed (/roadmap).
      const params: SignalReportListParams = { limit: 100, exclude_report_type: 'roadmap' };
      if (q.trim()) params.q = q.trim();
      if (customer) params.customer = customer;
      if (theme) params.theme = theme;
      if (isPortal) params.exclude_empty = true;
      const res = await signalsReportsApi.list(params);
      setItems(res.items);
    } catch {
      setError('Failed to load reports.');
    } finally {
      setLoading(false);
    }
  }, [q, customer, theme, isPortal]);

  useEffect(() => {
    const timer = setTimeout(load, 200);
    return () => clearTimeout(timer);
  }, [load]);

  // Optional-tag filter options derived from the loaded set (the agent isn't
  // required to tag; these only appear when present).
  const customerOptions = useMemo(
    () => Array.from(new Set(items.flatMap((r) => r.customers))).sort(),
    [items],
  );
  const themeOptions = useMemo(
    () => Array.from(new Set(items.flatMap((r) => r.themes))).sort(),
    [items],
  );

  const reset = () => {
    setQ('');
    setCustomer('');
    setTheme('');
  };

  return (
    <div className={styles.page}>
      {isPortal ? <PortalSignalsTabs /> : <SignalsTabs />}
      <header className={styles.head}>
        <div className={styles.headRow}>
          <div>
            <h1>Quality Signals — reports</h1>
            <p>
              The agent&apos;s reports, newest first. Each opens as a standalone
              page in a new tab.
            </p>
          </div>
          {!isPortal && (
            <button
              type="button"
              className={styles.runBtn}
              onClick={runNow}
              disabled={running}
              title="Queue an on-demand run of the reporting agent — the report lands in this feed when it finishes."
            >
              {running ? 'Queuing…' : 'Run report now'}
            </button>
          )}
        </div>
        {runNote && <div className={styles.runNote}>{runNote}</div>}
      </header>

      <div className={styles.filters}>
        <input
          className={styles.search}
          placeholder="Search title or summary…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select value={customer} onChange={(e) => setCustomer(e.target.value)}>
          <option value="">All customers</option>
          {customerOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select value={theme} onChange={(e) => setTheme(e.target.value)}>
          <option value="">All themes</option>
          {themeOptions.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <button type="button" className={styles.reset} onClick={reset}>
          Reset
        </button>
      </div>

      {loading && <div className={styles.muted}>Loading…</div>}
      {error && <div className={styles.error}>{error}</div>}
      {!loading && !error && items.length === 0 && (
        <div className={styles.muted}>
          No reports yet — they appear here once the agent delivers one.
        </div>
      )}

      <div className={styles.feed}>
        {items.map((r) => {
          const d = formatDay(r.report_date);
          return (
            // The whole card still opens the report (the title link is
            // stretched over it), but the customer and theme chips are their
            // own buttons that set the filters above: inside the old card
            // link they could only open the report (fan-out finding 14).
            <article key={r.id} className={styles.card}>
              <div className={styles.dateBadge}>
                <span className={styles.day}>{d.day}</span>
                <span className={styles.mon}>{d.mon}</span>
              </div>
              <div className={styles.cardBody}>
                <div className={styles.titleRow}>
                  <a
                    className={`${styles.title} ${styles.cardLink}`}
                    href={signalsReportsApi.viewPath(r.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {r.title}
                  </a>
                  {r.report_type && (
                    <span className={styles.type}>{cadenceLabel(r.report_type)}</span>
                  )}
                </div>
                <div className={styles.by}>
                  {r.author} · {new Date(r.created_at).toLocaleString()}
                </div>
                {r.summary && <div className={styles.summary}>{r.summary}</div>}
                <div className={styles.tags}>
                  {r.customers.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`${styles.tag} ${styles.tagBtn}`}
                      aria-pressed={customer === c}
                      title={customer === c ? 'Show every customer again' : `Show only reports about ${c}`}
                      data-testid="report-chip"
                      onClick={() => setCustomer(customer === c ? '' : c)}
                    >
                      {c}
                    </button>
                  ))}
                  {r.themes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className={`${styles.tag} ${styles.themeTag} ${styles.tagBtn}`}
                      aria-pressed={theme === t}
                      title={theme === t ? 'Show every theme again' : `Show only reports on ${t}`}
                      data-testid="report-chip"
                      onClick={() => setTheme(theme === t ? '' : t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <div className={styles.open} aria-hidden="true">Open full report ↗</div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
